import { useContext } from 'react';
import router from 'next/router';
import storageKeys from 'src/app_config/storage-keys.json';

import { Field, LinkField, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { ModalContext } from 'src/context/Modal/ModalContext';
import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';
import { WildCardRouteContext } from 'src/context/WildCardRoute/WildCardRouteContext';

import useContentAccessLevel from 'src/hooks/useContentAccessLevel';

import { ComponentProps, SitecoreContextValue } from 'src/lib/ComponentProps';

import { parseAccessLevelRouteFields } from 'src/mappers/fields/parseAccessLevelContent';

import { FieldEditFrame } from 'src/utils/customFieldEditFrame';
import { isLmsCourse, isRcniCPDArticle, isRcniLearningModule, isResourceContentPage, isResourcePage } from 'src/utils/isTemplate';

import { Button } from 'src/components/basicContent/_Button/Button';
import { EditorHelp } from 'src/components/layout/_EditorHelp/_EditorHelp';

import { CourseFields } from '../../courseOverview/CourseFields';
import WildCardFields from '../WildCardFields';

import styles from './ContentOverviewActionButton.module.css';

enum ContentType {
	Course,
	Resource,
}

enum Actions {
	Enrol,
	RegisterInterest,
	GoToCourse,
	RequireAdditionalPermissionsForCourse,
	GoToResource,
	RequireAdditionalPermissionsForResource,
}

type ContentOverviewActionButtonProps = ComponentProps & {
	registerYourInterestOnly?: boolean;
	removePadding?: boolean;
	fields?: WildCardFields &
		CourseFields & {
			RCNiArticleUrl?: Field<string>;
		};
};

export default function ContentOverviewActionButton(props: ContentOverviewActionButtonProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const { authenticatedUserProfile } = useContext(RcnUserContext);
	const WildCardRoute = useContext(WildCardRouteContext);
	const ModalState = useContext(ModalContext);

	const { isAllowedAccessToContent } = useContentAccessLevel(parseAccessLevelRouteFields(props.rendering.fields));

	function isUserEnroled() {
		if (!authenticatedUserProfile?.contact || !props.fields?.CrmCode?.value) {
			return false;
		}

		const activityCodes = authenticatedUserProfile?.contact?.activities?.map(
			(activity: { codeWithPrefix: string }) => activity.codeWithPrefix
		);

		if (activityCodes?.includes(props.fields.CrmCode.value)) {
			return true;
		} else if (typeof window !== 'undefined') {
			const recentlyEnrolledCourses = localStorage.getItem(storageKeys.LocalStorage.RecentlyEnrolledCourses);

			if (recentlyEnrolledCourses) {
				const enrolledCourses = recentlyEnrolledCourses?.split('|');

				if (
					enrolledCourses.find(
						(c) => c.toLowerCase() === `${authenticatedUserProfile?.contact?.contactId},${props.fields?.CrmCode?.value}`.toLowerCase()
					)
				) {
					return true;
				}
			}
		}

		return false;
	}

	if (!props?.fields) {
		return <></>;
	}

	const userCanSelfEnrol: boolean =
		props.fields?.UserCanSelfEnrol != undefined && props.fields.UserCanSelfEnrol.value === true && props.fields?.CrmCode?.value !== '';

	const templateId = WildCardRoute?.wildCardItemInfo ? WildCardRoute.wildCardItemInfo?.TemplateId : sitecoreContext.route.templateId;

	let contentType: ContentType | undefined = undefined;

	if (isResourcePage(templateId) || isResourceContentPage(templateId) || isRcniCPDArticle(templateId)) {
		contentType = ContentType.Resource;
	} else if (isLmsCourse(templateId) || isRcniLearningModule(templateId)) {
		contentType = ContentType.Course;
	}

	let scenario: Actions | undefined = undefined;

	if (isRcniLearningModule(templateId)) {
		if (isAllowedAccessToContent) {
			scenario = Actions.GoToCourse;
		} else {
			scenario = Actions.RequireAdditionalPermissionsForCourse;
		}
	} else if (contentType == ContentType.Course) {
		if (props.registerYourInterestOnly) {
			scenario = Actions.RegisterInterest;
		} else if (!userCanSelfEnrol) {
			scenario = Actions.RegisterInterest;
		} else if (isUserEnroled()) {
			scenario = Actions.GoToCourse;
		} else if (!isUserEnroled() && userCanSelfEnrol && isAllowedAccessToContent) {
			scenario = Actions.Enrol;
		} else if (userCanSelfEnrol && !isAllowedAccessToContent) {
			scenario = Actions.RequireAdditionalPermissionsForCourse;
		} else if (!isAllowedAccessToContent) {
			scenario = Actions.RequireAdditionalPermissionsForCourse;
		} else if (isAllowedAccessToContent) {
			scenario = Actions.GoToCourse;
		}
	} else if (contentType == ContentType.Resource) {
		if (!isAllowedAccessToContent) {
			scenario = Actions.RequireAdditionalPermissionsForResource;
		} else if (isAllowedAccessToContent) {
			scenario = Actions.GoToResource;
		}
	}

	function displayAppropriateButton() {
		switch (scenario) {
			case Actions.RegisterInterest:
				return (
					<Button
						type="button"
						title={sitecoreContext.dictionary.registerYourInterestButtonText}
						className={`${props.registerYourInterestOnly ? styles.registerYourInterestButton : 'orange button'}`}
						onClick={() =>
							ModalState.updateCourseRegisterInterestModal({
								Display: true,
								Data: { recipient: props.fields?.RegisterInterestEmailRecipient?.value ?? '' },
							})
						}
					>
						<Text field={{ value: sitecoreContext.dictionary.registerYourInterestButtonText }} />
					</Button>
				);
			case Actions.GoToCourse:
				const url = (props.rendering.fields?.CourseLink as LinkField)?.value?.href ?? props.fields?.RCNiArticleUrl?.value;

				return (
					<Button
						type="button"
						title={sitecoreContext.dictionary.viewCourseButtonText}
						className="orange button"
						onClick={() => router.push({ pathname: `${url}` })}
					>
						<Text field={{ value: sitecoreContext.dictionary.viewCourseButtonText }} />
					</Button>
				);
			case Actions.Enrol:
				return (
					<Button
						type="button"
						title={sitecoreContext.dictionary.enrolButtonText}
						className="orange button"
						onClick={() => ModalState.updateDisplayCourseEnrolModal(true)}
					>
						<Text field={{ value: sitecoreContext.dictionary.enrolButtonText }} />
					</Button>
				);
			case Actions.RequireAdditionalPermissionsForCourse:
				return (
					<Button
						type="button"
						title={sitecoreContext.dictionary.viewCourseButtonText}
						className="orange button"
						onClick={() => ModalState.updateDisplayLoginFormModal(true)}
					>
						<Text field={{ value: sitecoreContext.dictionary.viewCourseButtonText }} />
					</Button>
				);
			case Actions.RequireAdditionalPermissionsForResource:
				return (
					<Button
						type="button"
						title={sitecoreContext.dictionary.viewResourceButtonText}
						className="orange button"
						onClick={() => ModalState.updateDisplayLoginFormModal(true)}
					>
						<Text field={{ value: sitecoreContext.dictionary.viewResourceButtonText }} />
					</Button>
				);
			case Actions.GoToResource:
				const resourceUrl =
					props.fields?.RCNiArticleUrl?.value != null && props.fields?.RCNiArticleUrl?.value != ''
						? props.fields?.RCNiArticleUrl?.value
						: props.fields?.WildCardContentUrl;

				return (
					<Button
						type="button"
						title={sitecoreContext.dictionary.viewResourceButtonText}
						className="orange button"
						onClick={() => router.push({ pathname: resourceUrl })}
					>
						<Text field={{ value: sitecoreContext.dictionary.viewResourceButtonText }} />
					</Button>
				);
			default:
				return <></>;
		}
	}

	return (
		<section className={`maxWidth ${props.removePadding ? '' : 'padX'}`}>
			<FieldEditFrame
				itemId={WildCardRoute?.wildCardItemInfo?.Id ?? sitecoreContext?.itemId ?? undefined}
				fields={['RegisterInterestEmailRecipient', 'CrmCode', 'UserCanSelfEnrol', 'EnrolDisclaimerText']}
				header="Edit button"
			>
				<EditorHelp text="Click here to edit the button" />
				<div className={styles.buttonWrapper}>{displayAppropriateButton()}</div>
			</FieldEditFrame>
		</section>
	);
}
